import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { isForecastEnabled, isXeroEnabled } from '@/lib/feature-flags';
import { FinanceGuard } from '@/components/finance/FinanceGuard';
import { FinanceNav } from '@/components/tracking/FinanceNav';
import { FreshnessChip } from '@/components/tracking/FreshnessChip';
import { useFinancialModel } from '@/hooks/use-financial-model';
import { budgetVariance, type BudgetRag } from '@/lib/financial-calc/forecast';
import { grossProfit, totalOverhead, netProfitBeforeTax } from '@/lib/financial-calc/ratios';
import { fmtCurrency } from '@/components/tracking/format';
import { ForecastToolNav } from '@/components/finance/ForecastToolNav';

/**
 * /finance/forecast/budget-vs-actual — Budget vs Actual (F-013 S6.8). Real actual P&L lines vs a
 * user budget (seeded to actuals — honest until edited), with per-line variance + a direction-aware RAG.
 */
export const Route = createFileRoute('/finance/forecast_/budget-vs-actual')({
  beforeLoad: () => {
    if (!isXeroEnabled() || !isForecastEnabled()) {
      throw redirect({ to: '/finance', replace: true });
    }
  },
  component: () => (
    <FinanceGuard>
      <BudgetVsActual />
    </FinanceGuard>
  ),
});

const RAG_CLS: Record<BudgetRag, string> = { green: 'bg-success-light text-success', amber: 'bg-warning-light text-warning', red: 'bg-danger-light text-danger' };
const RAG_LABEL: Record<BudgetRag, string> = { green: 'On track', amber: 'Watch', red: 'Off' };

function BudgetVsActual() {
  const { model, input } = useFinancialModel();
  const periods = input?.periods ?? [];
  const rt = (periods[periods.length - 1]?.roleTotals ?? {}) as Record<string, number>;

  const actuals = useMemo(() => ({
    revenue: rt.revenue ?? 0,
    grossProfit: grossProfit(rt),
    overhead: totalOverhead(rt),
    netProfit: netProfitBeforeTax(rt),
  }), [rt]);

  // Budget FOLLOWS the loaded actuals until the builder edits a line (zero
  // variance at seed, still honest) — a plain useState captured the pre-load
  // zeros and never re-seeded once the model arrived (F-124 review #6).
  const [budgetOv, setBudgetOv] = useState<Partial<typeof actuals>>({});
  const budget = { ...actuals, ...budgetOv } as typeof actuals;
  const setBudget = (next: typeof actuals | ((b: typeof actuals) => typeof actuals)) =>
    setBudgetOv(typeof next === 'function' ? next(budget) : next);

  const lines: { key: keyof typeof actuals; label: string; dir: 'higher-better' | 'lower-better' }[] = [
    { key: 'revenue', label: 'Revenue', dir: 'higher-better' },
    { key: 'grossProfit', label: 'Gross profit', dir: 'higher-better' },
    { key: 'overhead', label: 'Overheads', dir: 'lower-better' },
    { key: 'netProfit', label: 'Net profit', dir: 'higher-better' },
  ];

  if (!model || periods.length === 0) {
    return (
      <div className="mx-auto w-full max-w-6xl">
        <FinanceNav />
        <div className="mt-8 rounded-2xl border border-dashed border-border py-16 text-center text-muted-foreground">
          <p className="font-semibold text-foreground">Needs your Xero data</p>
          <p className="mt-1 text-[12.5px]">Connect Xero and sync to compare against a budget.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl">
      <FinanceNav />
      <Link to="/finance/forecast" className="mt-2 inline-flex items-center gap-1 text-[12px] text-muted-foreground no-underline hover:text-foreground">
        <ChevronLeft className="h-3.5 w-3.5" /> Forecast
      </Link>
      <div className="mt-1 flex flex-wrap items-center justify-between gap-2"><h1 className="ds-h1">Budget vs Actual</h1><FreshnessChip fetchedAt={model?.fetchedAt} /></div>
      <p className="mb-4 mt-0.5 text-[13px] text-muted-foreground">This month against your budget — set the targets, see where you’re off. ({model.period})</p>

      <div className="overflow-x-auto rounded-2xl border border-border bg-card">
        <table className="w-full text-[12.5px]">
          <thead><tr className="border-b border-border text-left text-[10.5px] uppercase tracking-wide text-muted-foreground">
            <th className="px-3 py-2 font-bold">Line</th>
            <th className="px-3 py-2 text-right font-bold">Actual</th>
            <th className="px-3 py-2 text-right font-bold">Budget</th>
            <th className="px-3 py-2 text-right font-bold">Variance</th>
            <th className="px-3 py-2 text-right font-bold">Status</th>
          </tr></thead>
          <tbody>
            {lines.map((l) => {
              const v = budgetVariance(actuals[l.key], budget[l.key], l.dir);
              return (
                <tr key={l.key} className="border-b border-border/60 last:border-b-0">
                  <td className="px-3 py-2 font-semibold text-foreground">{l.label}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtCurrency(v.actual)}</td>
                  <td className="px-3 py-2 text-right">
                    <input type="number" value={budget[l.key]} step={5000}
                      onChange={(e) => setBudget((b) => ({ ...b, [l.key]: parseFloat(e.target.value) || 0 }))}
                      className="w-28 rounded-md border border-border bg-background px-2 py-1 text-right text-base sm:text-[12px] text-foreground" />
                  </td>
                  <td className={`px-3 py-2 text-right font-mono tabular-nums ${v.variance === 0 ? 'text-muted-foreground' : ''}`}>
                    {v.variance > 0 ? '+' : ''}{fmtCurrency(v.variance)}<span className="ml-1 text-[10px] text-muted-foreground">({v.variancePct > 0 ? '+' : ''}{v.variancePct.toFixed(0)}%)</span>
                    <div className="mt-1 h-1.5 overflow-hidden rounded bg-muted">
                      <div className={`h-1.5 rounded ${v.rag === 'green' ? 'bg-success/70' : v.rag === 'amber' ? 'bg-warning/70' : 'bg-danger/70'}`}
                        style={{ width: `${Math.min(100, Math.abs(v.variancePct))}%` }} />
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right"><span className={`inline-block rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${RAG_CLS[v.rag]}`}>{RAG_LABEL[v.rag]}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-4 text-[11px] text-muted-foreground">Actuals are your real Xero figures; the budget is yours to set (seeded to actuals). Revenue/profit below budget and costs above budget flag amber/red.</p>
      <ForecastToolNav current="budget-vs-actual" />
    </div>
  );
}
